import { useState, useEffect, useRef, useCallback } from "react";
import { Plus, Search, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MASTER_DEFS } from "../config/masterDefs";

// Special (non db.masters) sources: existing Customer & Product collections.
const SPECIAL = {
  product: {
    label: "Product / Item",
    endpoint: "/products",
    listParams: (s) => ({ search: s, status: "active" }),
    normalize: (p) => ({ ...p, name: p.itemName, _display: `${p.productCode || "—"} · ${p.itemName}` }),
    addFields: [["itemName", "Item Name *"], ["productCode", "Product Code"], ["defaultUnitRate", "Default Rate"]],
    build: (f) => ({ itemName: f.itemName || f.name || "", productCode: f.productCode || "", defaultUnitRate: Number(f.defaultUnitRate) || 0, stdNonStd: "Std" }),
    addPerm: "add_product",
  },
  customer: {
    label: "Customer",
    endpoint: "/customers",
    listParams: (s) => ({ search: s, status: "active" }),
    normalize: (c) => ({ ...c, name: c.customerName, _display: c.customerName }),
    addFields: [["customerName", "Customer Name *"], ["consigneeAddress", "Consignee Address"], ["gstNumber", "GST No."], ["contactPerson", "Contact Person"], ["mobile", "Mobile"]],
    build: (f) => ({ customerName: f.customerName || f.name || "", consigneeAddress: f.consigneeAddress || "", deliveryAddress: f.consigneeAddress || "", gstNumber: f.gstNumber || "", contactPerson: f.contactPerson || "", mobile: f.mobile || "" }),
    addPerm: "add_customer",
  },
};

function buildSource(master) {
  if (SPECIAL[master]) return { ...SPECIAL[master], endpoint: SPECIAL[master].endpoint, isMaster: false };
  const def = MASTER_DEFS[master] || { label: master, fields: [] };
  return {
    label: def.label,
    endpoint: `/masters/${master}`,
    listParams: (s) => ({ search: s }),
    normalize: (m) => ({ ...m, _display: m.name }),
    addFields: [["name", `${def.label} *`], ...def.fields],
    build: (f) => ({ name: f.name || "", ...Object.fromEntries(def.fields.map(([k]) => [k, f[k] || ""])) }),
    addPerm: "add_master",
    isMaster: true,
  };
}

export default function SearchSelectAddNew({ master, value, onChange, onSelect, disabled, placeholder, testid }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const can = (p) => isAdmin || (user?.permissions || []).includes(p);
  const src = buildSource(master);
  const canAdd = can(src.addPerm);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({});
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);
  const debRef = useRef(null);

  const fetchResults = useCallback(async (s) => {
    setLoading(true);
    try {
      const { data } = await api.get(src.endpoint, { params: src.listParams(s) });
      setResults((data || []).map(src.normalize));
    } catch { setResults([]); }
    setLoading(false);
  }, [master]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => fetchResults(query), 220);
    return () => clearTimeout(debRef.current);
  }, [query, open, fetchResults]);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const choose = (rec) => { onSelect?.(rec); onChange?.(rec.name); setOpen(false); setQuery(""); };

  const openAdd = () => {
    const init = {};
    src.addFields.forEach(([k]) => (init[k] = ""));
    // seed the name-ish field with the current query
    const nameKey = src.addFields[0][0];
    init[nameKey] = query || value || "";
    setAddForm(init); setAddOpen(true);
  };

  const saveNew = async () => {
    const nameKey = src.addFields[0][0];
    if (!(addForm[nameKey] || "").trim()) { toast.error(`${src.addFields[0][1].replace(" *", "")} is required.`); return; }
    setSaving(true);
    try {
      const { data } = await api.post(src.endpoint, src.build(addForm));
      const rec = src.normalize(data);
      if (data._duplicate) toast.warning("This record already exists — selected the existing one.");
      else toast.success(`${src.label} added`);
      choose(rec); setAddOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <input
          className="input pr-8"
          value={open ? query : (value || "")}
          placeholder={placeholder || `Search ${src.label}...`}
          disabled={disabled}
          onFocus={() => { if (!disabled) { setOpen(true); setQuery(""); fetchResults(""); } }}
          onChange={(e) => { setQuery(e.target.value); onChange?.(e.target.value); }}
          data-testid={testid}
        />
        <ChevronDown className="h-4 w-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-3 py-2 text-xs text-slate-400 flex items-center gap-2"><Search className="h-3.5 w-3.5" /> Searching…</div>}
          {!loading && results.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No matches. Add a new one below.</div>}
          {results.map((r) => (
            <button key={r.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 border-b border-slate-50 last:border-0"
              onClick={() => choose(r)} data-testid={`${testid}-opt-${r.id}`}>
              {r._display}
            </button>
          ))}
          {canAdd && (
            <button type="button" className="w-full text-left px-3 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-50 flex items-center gap-2 sticky bottom-0 bg-white border-t border-slate-100"
              onClick={openAdd} data-testid={`${testid}-add-new`}>
              <Plus className="h-4 w-4" /> Add New {src.label}
            </button>
          )}
        </div>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onMouseDown={(e) => { e.stopPropagation(); }} onClick={() => setAddOpen(false)}>
          <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-add-modal`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-lg font-bold">Add {src.label}</h3>
              <button onClick={() => setAddOpen(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-3">
              {src.addFields.map(([k, l]) => (
                <div key={k}>
                  <label className="label">{l}</label>
                  <input className="input" value={addForm[k] || ""} onChange={(e) => setAddForm((p) => ({ ...p, [k]: e.target.value }))} data-testid={`${testid}-add-${k}`} />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="btn-outline" onClick={() => setAddOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={saveNew} disabled={saving} data-testid={`${testid}-add-save`}>{saving ? "Saving…" : "Save & Select"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
